import { patch, post } from 'stores/_services/dataService';
import { action, observable, makeObservable, runInAction } from 'mobx';
import { request } from '../../services/helpers/request';
import { SortDirection } from 'react-virtualized';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

export interface IAgent {
    id: string;
    name: string;
    metricsCollection?: string;
}

interface IRollbackMetrics {
    opNewFile: number;
    opOverwriteFile: number;
    opModifyData: number;
    opAppendData: number;
    opTruncateFile: number;
    opDeleteFile: number;
    opRenameFile: number;
    opRollbackBoundary: number;
    startTimeFileTime: string;
    endTimeFileTime: string;
}

interface IRWMetrics {
    cntCreate: number;
    cntDelete: number;
    cntUpdate: number;
    cntRename: number;
    cntExc: number;
}

interface IAgentMetricsData {
    dbSize?: number;
    systemInfo?: any;
    queryTimes?: any;
    logDirSize?: number;
    ligerInfo?: any;
    stats?: any;
    rollbackMetrics?: IRollbackMetrics;
    rwMetrics?: IRWMetrics;
}

interface IAgentMetrics {
    agentId: string;
    data: IAgentMetricsData;
    id: string;
    timestamp: string;
}

export default class AgentStore {
    agent: IAgent = {
        id: '',
        name: ''
    };
    targetGroupMembership = [];
    systemMetrics = [];
    sizeMetrics = [];
    queryMetrics = [];
    simpleCacheMetrics = [];
    rollbackMetrics = [];
    rwMetrics = [];

    constructor(agent: IAgent) {
        makeObservable(this, {
            agent: observable,
            targetGroupMembership: observable,
            systemMetrics: observable,
            sizeMetrics: observable,
            queryMetrics: observable,
            simpleCacheMetrics: observable,
            rollbackMetrics: observable,
            rwMetrics: observable,
            update: action,
            clearDetails: action,
            saveAgentName: action,
            getMetrics: action
        });

        this.update(agent);
    }

    update(agent: IAgent): void {
        this.agent = {
            ...this.agent,
            ...agent
        };
    }

    clearDetails(): void {
        this.targetGroupMembership = [];
        this.systemMetrics = [];
        this.sizeMetrics = [];
        this.queryMetrics = [];
        this.simpleCacheMetrics = [];
        this.rollbackMetrics = [];
        this.rwMetrics = [];
    }

    async saveAgentName(name: string): Promise<void> {
        await patch(`agents/${this.agent.id}/rename`, { name });
        this.agent.name = name;
    }

    async getTargetGroupMembership(): Promise<void> {
        const filter = {
            where: { agentId: this.agent.id },
            include: {
                relation: 'target',
                scope: { fields: ['data', 'name'] }
            }
        };

        const response: any = await request(`/api/addresses?filter=${JSON.stringify(filter)}`);
        //every step that updates observables in an asynchronous process should be marked as action(one way to use is runInAction), Only synchronous changes to observable data works in actions
        runInAction(() => {
            this.targetGroupMembership = response.items
                .filter((item: any) => item.target)
                .map((item: any) => {
                    return {
                        id: item.id,
                        name: item.target.name || '',
                        targetId: item.targetId
                    };
                });
        });
    }

    async getMetrics(startTime: string): Promise<void> {
        const filter = {
            where: {
                agentId: this.agent.id,
                timestamp: { gt: startTime }
            },
            order: `timestamp ${SortDirection.ASC}`
        };

        const res: any = await request(`/api/agentmetrics?filter=${JSON.stringify(filter)}`);

        const items: any = [];
        const rbItems: any = [];
        const rwItems: any = [];

        res.items.forEach((item: IAgentMetrics) => {
            if (!!item.data?.systemInfo) {
                items.push(item);
            }
            if (!!item.data?.rollbackMetrics) {
                rbItems.push(item);
            }
            if (!!item.data?.rwMetrics) {
                rwItems.push(item);
            }
        });

        runInAction(() => {
            this.rollbackMetrics = rbItems.map((item: any) => ({
                timestamp: dayjs.utc(item.timestamp).valueOf(),
                'New Files': item.data.rollbackMetrics.opNewFile || 0,
                'Overwrite File': item.data.rollbackMetrics.opOverwriteFile || 0,
                'Modify Data': item.data.rollbackMetrics.opModifyData || 0,
                'Append Data': item.data.rollbackMetrics.opAppendData || 0,
                'Truncate File': item.data.rollbackMetrics.opTruncateFile || 0,
                'Delete File': item.data.rollbackMetrics.opDeleteFile || 0,
                'Rename File': item.data.rollbackMetrics.opRenameFile || 0,
                'Rollback Boundary': item.data.rollbackMetrics.opRollbackBoundary || 0
            }));

            this.rwMetrics = rwItems.map((item: any) => ({
                timestamp: dayjs.utc(item.timestamp).valueOf(),
                Create: item.data.rwMetrics.cntCreate,
                Delete: item.data.rwMetrics.cntDelete,
                Update: item.data.rwMetrics.cntUpdate,
                Rename: item.data.rwMetrics.cntRename,
                Exc: item.data.rwMetrics.cntExc
            }));

            this.sizeMetrics = items.map((item: any) => ({
                'Cache Size': (item.data.dbSize / 1024 / 1024).toFixed(2),
                'Log Size': (item.data.logDirSize / 1024 / 1024).toFixed(2),
                timestamp: dayjs.utc(item.timestamp).valueOf()
            }));

            this.systemMetrics = items.map((item: any) => {
                let agentCpu = 0;
                let agentMemory = 0;

                if (item.data.ligerInfo) {
                    agentCpu = item.data.ligerInfo.cpuLoad || 0;
                    agentMemory = item.data.ligerInfo.ramUsage || 0;
                }

                return {
                    'CPU Usage': (item.data.systemInfo.cpuLoad || 0).toFixed(2),
                    'Memory Usage': (item.data.systemInfo.ramUsage || 0).toFixed(2),
                    'Agent CPU Usage': agentCpu.toFixed(2),
                    'Agent Memory Usage': agentMemory.toFixed(2),
                    timestamp: dayjs.utc(item.timestamp).valueOf()
                };
            });

            this.queryMetrics = items.map((item: any) => ({
                Artifacts: item.data.queryTimes.surveyArtifact || 0,
                AutoStarts: item.data.queryTimes.surveyAutoStart || 0,
                Connections: item.data.queryTimes.connectionItem || 0,
                Drivers: item.data.queryTimes.surveyDriver || 0,
                Memory: item.data.queryTimes.surveyMemScan || 0,
                Modules: item.data.queryTimes.surveyModule || 0,
                Processes: item.data.queryTimes.surveyProcess || 0,
                'RTS Processes': item.data.queryTimes.rtsProcess || 0,
                'RTS Memory': item.data.queryTimes.rtsMemScan || 0,
                Users: item.data.queryTimes.userItem || 0,
                timestamp: dayjs.utc(item.timestamp).valueOf()
            }));

            this.simpleCacheMetrics = items.map((item: any) => {
                const latencies = [
                    item.data.queryTimes.surveyArtifact || 0,
                    item.data.queryTimes.surveyAutoStart || 0,
                    item.data.queryTimes.connectionItem || 0,
                    item.data.queryTimes.surveyDriver || 0,
                    item.data.queryTimes.surveyMemScan || 0,
                    item.data.queryTimes.surveyModule || 0,
                    item.data.queryTimes.surveyProcess || 0,
                    item.data.queryTimes.rtsProcess || 0,
                    item.data.queryTimes.rtsMemScan || 0,
                    item.data.queryTimes.userItem || 0
                ];

                return {
                    Maximum: Math.max(...latencies).toFixed(2),
                    Average: (latencies.reduce((a, b) => a + b, 0) / latencies.length).toFixed(2),
                    timestamp: dayjs.utc(item.timestamp).valueOf()
                };
            });
        });
    }

    public async toggleMetricsCollection(agentId: string, metricsCollection: string) {
        const { data } = await post(`agents/${agentId}/toggleMetricsCollection`, { metricsCollection });

        runInAction(() => {
            this.agent = { ...this.agent, metricsCollection: data };
        });

        return data;
    }
}
